// @ts-strict-ignore
import React from "react";

export type AnalyticsDataMode = "live" | "mock";

const STORAGE_KEY = "analytics.dataMode";

export const AnalyticsModeContext = React.createContext<{
  mode: AnalyticsDataMode;
  setMode: (m: AnalyticsDataMode) => void;
  isLive: boolean;
} | null>(null);

export const AnalyticsModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = React.useState<AnalyticsDataMode>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as AnalyticsDataMode | null;

      return saved === "mock" || saved === "live" ? saved : "live";
    } catch {
      return "live";
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      // ignore persistence errors (e.g., private mode)
      void e;
    }
  }, [mode]);

  const value = React.useMemo(() => ({ mode, setMode, isLive: mode === "live" }), [mode]);

  return <AnalyticsModeContext.Provider value={value}>{children}</AnalyticsModeContext.Provider>;
};

export const useAnalyticsMode = () => {
  const ctx = React.useContext(AnalyticsModeContext);

  if (!ctx) throw new Error("useAnalyticsMode must be used within AnalyticsModeProvider");

  return ctx;
};
